import React, { useState, useEffect, useCallback } from "react";
import {
  FaBars,
  FaSignOutAlt,
  FaCogs,
  FaCog,
  FaCloudUploadAlt,
  FaHome,
  FaMoon,
  FaSun,
  FaUserCircle,
  FaArrowLeft,
  FaPaperPlane,
  FaBell,
  FaTimes,
  FaUpload,
  FaEnvelope,
  FaCheckCircle,
  FaCheck,
  FaEye,
  FaRegCircle,
  FaCircle,
  FaDownload,
  FaCrown,
  FaExternalLinkAlt,
  FaShieldAlt,
  FaPaintBrush,
  FaTools,
  FaFileInvoiceDollar
} from "react-icons/fa";
import { useNavigate, useLocation } from "react-router-dom";
import {
  Spinner,
  Button,
  Card,
  Container,
  Row,
  Col,
  Alert,
  Modal,
  Badge,
  Dropdown,
  Image,
  Form,
  ListGroup
} from "react-bootstrap";
import { isAuthenticated } from "../services/authService";
import { useTheme } from "../ThemeContext";
import { useUiVersion } from "../UiVersionContext";
import { resolveScreen } from "./screenRegistry";
import "./dashboard.css";
import WidgetCreator from "./WidgetCreator";
import { apifetch } from '../services/interceptors';
import { startProCheckout } from '../utils/billing';
import "./UpgradeModal.css";

function Dashboard() {
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // Allow deep-linking into a Dashboard tab via ?tab=Profile (e.g. the MFA promo banner's
  // "Set up now" button routes here to jump straight to Profile > Two-Factor Authentication).
  const initialTabParam = new URLSearchParams(window.location.search).get('tab');
  const [activeMenu, setActiveMenu] = useState(initialTabParam || 'Home');
  const { isDarkMode, setIsDarkMode } = useTheme();
  const { syncFromServer: syncUiVersion } = useUiVersion();
  const [showLogoutModal, setShowLogoutModal] = useState(false);
  const [showLogoUploadModal, setShowLogoUploadModal] = useState(false);
  const [logoUploading, setLogoUploading] = useState(false);
  const [logoUploadError, setLogoUploadError] = useState("");
  const [logoUploadSuccess, setLogoUploadSuccess] = useState("");
  const [selectedFile, setSelectedFile] = useState(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 992);
  const [showNotifications, setShowNotifications] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [loadingNotifications, setLoadingNotifications] = useState(false);
  const [subscriptionType, setSubscriptionType] = useState(null);
  const [isPro, setIsPro] = useState(false);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [showUpdateBanner, setShowUpdateBanner] = useState(false);
  const [latestVersion, setLatestVersion] = useState("1.4.8");
  const navigate = useNavigate();
  const location = useLocation();

  // Keep the active tab in sync with ?tab=... even when already mounted on /dashboard
  // (e.g. the MFA promo banner's "Set up now" button navigating to /dashboard?tab=Profile
  // while the user is already on the dashboard doesn't remount this component, so the
  // useState initializer above never re-runs on its own).
  useEffect(() => {
    const tab = new URLSearchParams(location.search).get('tab');
    if (tab) setActiveMenu(tab);
  }, [location.search]);

  // Handle update banner visibility and fetching latest version
  useEffect(() => {
    const checkUpdateBanner = async () => {
      let fetchedVersion = "1.4.8";
      try {
        const response = await apifetch("s1/latest_version");
        if (response.ok) {
          const data = await response.json();
          if (data.version) {
            fetchedVersion = data.version;
            setLatestVersion(data.version);
          }
        }
      } catch (err) {
        console.error("Failed to fetch latest version for banner:", err);
      }

      const dismissed = localStorage.getItem("updateBannerDismissed");
      const firstShown = localStorage.getItem("updateBannerFirstShown");
      const storedVersion = localStorage.getItem("updateBannerVersion");

      // Reset dismissed state if version changes
      if (storedVersion !== fetchedVersion) {
        localStorage.removeItem("updateBannerDismissed");
        localStorage.removeItem("updateBannerFirstShown");
        localStorage.setItem("updateBannerVersion", fetchedVersion);
      }

      const isDismissedNow = localStorage.getItem("updateBannerDismissed") === "true";
      if (isDismissedNow) {
        setShowUpdateBanner(false);
        return;
      }

      const now = Date.now();
      const fiveDaysInMs = 5 * 24 * 60 * 60 * 1000;
      const firstShownTimeStr = localStorage.getItem("updateBannerFirstShown");

      if (!firstShownTimeStr) {
        localStorage.setItem("updateBannerFirstShown", now.toString());
        setShowUpdateBanner(true);
      } else {
        const firstShownTime = parseInt(firstShownTimeStr, 10);
        if (now - firstShownTime < fiveDaysInMs) {
          setShowUpdateBanner(true);
        } else {
          setShowUpdateBanner(false);
        }
      }
    };

    checkUpdateBanner();
  }, []);

  const handleDismissUpdateBanner = () => {
    localStorage.setItem("updateBannerDismissed", "true");
    localStorage.setItem("updateBannerVersion", latestVersion);
    setShowUpdateBanner(false);
  };

  useEffect(() => {
    if (isAuthenticated()) {
      // If the user just returned from a successful Stripe Checkout, confirm + grant Pro
      // right away (doesn't rely on the webhook reaching the server), THEN read status.
      confirmBillingReturnThenFetch();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // After returning from Stripe Checkout (/dashboard?billing=success&session_id=...),
  // confirm the session server-side so Pro is granted immediately, then refresh status
  // and strip the billing params from the URL.
  const confirmBillingReturnThenFetch = async () => {
    try {
      const params = new URLSearchParams(window.location.search);
      const sessionId = params.get('session_id');
      if (params.get('billing') === 'success' && sessionId) {
        const token = localStorage.getItem('token');
        await apifetch('s1/api/billing/confirm-checkout', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(token ? { Authorization: `Bearer ${token}` } : {})
          },
          body: JSON.stringify({ sessionId })
        }).catch(() => {});
        // Clean the URL so a refresh doesn't re-run this and the params don't linger.
        params.delete('billing');
        params.delete('session_id');
        const clean = window.location.pathname + (params.toString() ? `?${params.toString()}` : '');
        window.history.replaceState({}, '', clean);
      }
    } catch (e) {
      // non-fatal — fall through to a normal subscription fetch
    } finally {
      fetchSubscription();
    }
  };

  const fetchSubscription = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await apifetch('s1/api/subscription', {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        const subscription = data.subscription_type || 'community';
        const proStatus = data.is_pro || false;
        setSubscriptionType(subscription);
        setIsPro(proStatus);

        // Show upgrade modal if user is not Pro
        if (!proStatus) {
          setShowUpgradeModal(true);
        }
      }
    } catch (error) {
      console.error('Error fetching subscription:', error);
      // Default to community on error
      setSubscriptionType('community');
      setIsPro(false);
      // Show upgrade modal on error (assuming community)
      setShowUpgradeModal(true);
    }
  };

  const handleUpgradeClick = () => {
    // In-app Stripe checkout.
    startProCheckout();
  };

  // Handle responsiveness
  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth < 992;
      setIsMobile(mobile);

      if (mobile && sidebarOpen) {
        setSidebarOpen(false);
      }

      if (!mobile && !sidebarOpen) {
        setSidebarOpen(true);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [sidebarOpen]);

  // Theme styles with CSS variables for better theming
  const themeStyles = {
    '--bg-primary': isDarkMode ? "#121212" : "#f8f9fa",
    '--bg-secondary': isDarkMode ? "#1e1e1e" : "#ffffff",
    '--text-primary': isDarkMode ? "#ffffff" : "#212529",
    '--text-secondary': isDarkMode ? "#aaaaaa" : "#6c757d",
    '--border-color': isDarkMode ? "#444" : "#dee2e6",
    '--accent-color': isDarkMode ? "#bb86fc" : "#007bff",
    '--hover-bg': isDarkMode ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.05)",
    '--shadow': isDarkMode
      ? "0 4px 20px rgba(0,0,0,0.3)"
      : "0 4px 20px rgba(0,0,0,0.05)",
    '--card-radius': '12px',
    '--transition-speed': '0.3s'
  };

  const toggleSidebar = useCallback(() => {
    setSidebarOpen(prev => !prev);
  }, []);

  const fetchDashboardData = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await apifetch('s1/api/me', {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch dashboard data');
      }

      const data = await response.json();
      setDashboardData(data);
      // The stored preference is the source of truth; the localStorage cache only
      // decided what to paint before this reply arrived. Reconcile now so a change
      // made in another browser takes effect here.
      syncUiVersion(data?.ui_version);
      setLoading(false);
    } catch (error) {
      setError(error.message);
      setLoading(false);
    }
  };

  const fetchNotifications = async () => {
    try {
      setLoadingNotifications(true);
      const token = localStorage.getItem('token');
      const response = await apifetch('s1/api/notifications?limit=10', {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch notifications');
      }

      const data = await response.json();
      if (data.success) {
        setNotifications(data.data || []);
      }
    } catch (error) {
      console.error('Error fetching notifications:', error);
    } finally {
      setLoadingNotifications(false);
    }
  };

  const markNotificationAsRead = async (notificationId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await apifetch('s1/api/notifications/read', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({ notificationId })
      });

      if (!response.ok) {
        throw new Error('Failed to mark notification as read');
      }

      // Update local state
      setNotifications(prev =>
        prev.map(n =>
          n.notification_id === notificationId ? { ...n, is_read: 1 } : n
        )
      );

      // Update unread count
      setDashboardData(prev => ({
        ...prev,
        unreadCount: Math.max(0, prev.unreadCount - 1)
      }));
    } catch (error) {
      console.error('Error marking notification as read:', error);
    }
  };

  const markAllNotificationsAsRead = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await apifetch('s1/api/notifications/read-all', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error('Failed to mark all notifications as read');
      }

      // Update local state
      setNotifications(prev => prev.map(n => ({ ...n, is_read: 1 })));

      // Reset unread count
      setDashboardData(prev => ({
        ...prev,
        unreadCount: 0
      }));
    } catch (error) {
      console.error('Error marking all notifications as read:', error);
    }
  };

  useEffect(() => {
    if (!isAuthenticated()) {
      navigate('/login');
    } else {
      fetchDashboardData();
    }
  }, []);

  useEffect(() => {
    if (showNotifications) {
      fetchNotifications();
    }
  }, [showNotifications]);

  const handleLogout = () => {
    setShowLogoutModal(true);
  };

  const confirmLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('userData');
    setShowLogoutModal(false);
    navigate('/login');
  };

  const handleMenuClick = (menu) => {
    setActiveMenu(menu);
    if (isMobile) {
      setSidebarOpen(false);
    }
  };

  const handleLogoClick = () => {
    if (dashboardData?.isadmin === 1) {
      setShowLogoUploadModal(true);
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (!file.type.startsWith('image/')) {
        setLogoUploadError('Please select an image file');
        return;
      }

      if (file.size > 5 * 1024 * 1024) {
        setLogoUploadError('File size must be less than 5MB');
        return;
      }

      setSelectedFile(file);
      setLogoUploadError('');
    }
  };

  const uploadLogo = async () => {
    if (!selectedFile) {
      setLogoUploadError('Please select a file first');
      return;
    }

    setLogoUploading(true);
    setLogoUploadError('');
    setLogoUploadSuccess('');

    try {
      const token = localStorage.getItem('token');
      const formData = new FormData();
      formData.append('logo', selectedFile);

      const response = await apifetch('s1/api/tenant_logo', {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
        body: formData
      });

      if (!response.ok) {
        throw new Error('Failed to upload logo');
      }

      const data = await response.json();
      setLogoUploadSuccess('Logo uploaded successfully!');

      setDashboardData(prev => ({
        ...prev,
        tenant_logo: data.logo
      }));

      setTimeout(() => {
        setSelectedFile(null);
        setShowLogoUploadModal(false);
        setLogoUploadSuccess('');
      }, 2000);

    } catch (error) {
      setLogoUploadError(error.message);
    } finally {
      setLogoUploading(false);
    }
  };

  // Resolved through the shared registry so this shell and ShellV2 mount the same
  // screens. The registry also closes two long-standing gaps in the switch this
  // replaced: 'Notifications' had no case and fell through to the organization list,
  // and the default branch dropped the isAdmin prop that 'Home' passes.
  const renderContent = () => {
    const { Component, props } = resolveScreen(activeMenu, dashboardData);
    return <Component {...props} />;
  };

  if (loading) {
    return (
      <div className="loading-container">
        <Spinner animation="border" variant="primary" />
        <p className="mt-3">Loading your dashboard...</p>
      </div>
    );
  }

  return (
    <div className="dashboard-container" style={themeStyles}>
      {/* Top Navigation Bar */}
      <nav className="dashboard-navbar">
        <div className="navbar-left">
          <Button
            variant="link"
            className="sidebar-toggle"
            onClick={toggleSidebar}
          >
            <FaBars size={20} />
          </Button>

          <h4 className="brand-name">
            Pipsqueak - {subscriptionType ? subscriptionType.charAt(0).toUpperCase() + subscriptionType.slice(1) : ''}
          </h4>
        </div>

        <div className="navbar-right">
          {/* Notification Bell */}
          <Dropdown show={showNotifications} onToggle={setShowNotifications}>
            <Dropdown.Toggle variant="link" className="notification-btn">
              <FaBell size={18} />
              {dashboardData?.unreadCount > 0 && (
                <span className="notification-badge">{dashboardData.unreadCount}</span>
              )}
            </Dropdown.Toggle>
            <Dropdown.Menu className="notification-dropdown" align="end">
              <div className="notification-header">
                <h6>Notifications</h6>
                {dashboardData?.unreadCount > 0 && (
                  <Button
                    variant="link"
                    size="sm"
                    onClick={markAllNotificationsAsRead}
                    className="mark-all-read"
                  >
                    <FaCheck className="me-1" />
                    Mark all as read
                  </Button>
                )}
              </div>
              <div className="notification-list">
                {loadingNotifications ? (
                  <div className="notification-loading">
                    <Spinner animation="border" size="sm" />
                    <span className="ms-2">Loading notifications...</span>
                  </div>
                ) : notifications.length > 0 ? (
                  notifications.map(notification => (
                    <div
                      key={notification.notification_id}
                      className={`notification-item ${notification.is_read ? 'read' : 'unread'}`}
                    >
                      <div className="notification-icon">
                        <FaEnvelope />
                      </div>
                      <div className="notification-content">
                        <p className="notification-message">{notification.message}</p>
                        <small className="notification-time">
                          {new Date(notification.created_at).toLocaleString()}
                        </small>
                      </div>
                      <div className="notification-actions">
                        {!notification.is_read && (
                          <Button
                            variant="link"
                            size="sm"
                            className="mark-as-read-btn"
                            title="Mark as read"
                            onClick={() => markNotificationAsRead(notification.notification_id)}
                          >
                            <FaCheckCircle />
                          </Button>
                        )}
                        <div className={`notification-status ${notification.is_read ? 'read' : 'unread'}`}>
                          {notification.is_read ? <FaCheck /> : <FaCircle />}
                        </div>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="notification-empty">
                    <FaEnvelope className="mb-2" />
                    <p>No notifications</p>
                  </div>
                )}
              </div>
              <div className="notification-footer">
                <Button
                  variant="link"
                  size="sm"
                  onClick={() => {
                    setActiveMenu('Notifications');
                    setShowNotifications(false);
                  }}
                >
                  View all notifications
                </Button>
              </div>
            </Dropdown.Menu>
          </Dropdown>

          {/* Dark Mode Toggle */}
          <Button
            variant={isDarkMode ? 'outline-dark' : 'outline-light'}
            onClick={() => setIsDarkMode(prev => !prev)}
            className="theme-toggle1"
            size="sm"
          >
            {isDarkMode ? <FaSun size={16} color={"white"} /> : <FaMoon size={16} color={'black'} />}
          </Button>

          {/* User Profile */}
          <Dropdown className="user-dropdown">
            <Dropdown.Toggle variant="link" className="user-toggle">
              <div className="user-info">
                <FaUserCircle size={24} />
                <div className="user-details">
                  <div className="user-name">
                    {dashboardData?.name || "Guest"}
                  </div>
                  <div className="user-role">
                    {dashboardData?.isadmin === 1 ? 'Administrator' : 'User'}
                  </div>
                </div>
              </div>
            </Dropdown.Toggle>
            <Dropdown.Menu className="dropdown-menu-custom" align="end">
              <Dropdown.Item
                onClick={() => handleMenuClick("Profile")}
                className="dropdown-item-custom"
              >
                <FaCogs className="dropdown-icon" /> Profile Settings
              </Dropdown.Item>
              <Dropdown.Divider />
              <Dropdown.Item
                onClick={handleLogout}
                className="dropdown-item-custom"
              >
                <FaSignOutAlt className="dropdown-icon" /> Logout
              </Dropdown.Item>
            </Dropdown.Menu>
          </Dropdown>
        </div>
      </nav>

      {/* Sidebar */}
      <aside className={`dashboard-sidebar ${sidebarOpen ? 'open' : 'closed'}`}>
        <div className="sidebar-header">
          {/* Tenant Logo in Sidebar - Made larger */}
          <div
            className={`tenant-logo-container ${dashboardData?.isadmin === 1 ? 'clickable' : ''}`}
            onClick={handleLogoClick}
            title={dashboardData?.isadmin === 1 ? 'Click to upload new logo' : ''}
          >
            {dashboardData?.tenant_logo ? (
              <Image
                src={dashboardData.tenant_logo}
                alt="Tenant Logo"
                className="tenant-logo-sidebar"
              />
            ) : (
              <div className="tenant-logo-placeholder-sidebar">
                <FaUpload size={24} />
                <span>Upload Logo</span>
              </div>
            )}
          </div>

          <Button variant="link" onClick={toggleSidebar} className="sidebar-close">
            <FaArrowLeft size={16} />
          </Button>
        </div>

        <div className="sidebar-menu">
          <div
            className={`menu-item ${activeMenu === "Home" ? "active" : ""}`}
            onClick={() => handleMenuClick("Home")}
          >
            <FaHome className="menu-icon" />
            <span>Dashboard</span>
          </div>

          <div
            className={`menu-item ${activeMenu === "Upload" ? "active" : ""}`}
            onClick={() => handleMenuClick("Upload")}
          >
            <FaCloudUploadAlt className="menu-icon" />
            <span>Upload Data</span>
          </div>

          {isPro && (<div
            className={`menu-item ${activeMenu === "DomainScanner" ? "active" : ""}`}
            onClick={() => handleMenuClick("DomainScanner")}
          >
            <FaShieldAlt className="menu-icon" />
            <span>External Risk Scan</span>
          </div>)}

          {isPro && (
            <div
              className={`menu-item ${activeMenu === "ReportTemplates" ? "active" : ""}`}
              onClick={() => handleMenuClick("ReportTemplates")}
            >
              <FaPaintBrush className="menu-icon" />
              <span>Report Templates</span>
            </div>
          )}

          {dashboardData?.isadmin === 1 && (
            <div
              className={`menu-item ${activeMenu === "Adminpanel" ? "active" : ""}`}
              onClick={() => handleMenuClick("Adminpanel")}
            >
              <FaCog className="menu-icon" />
              <span>Admin Panel</span>
            </div>
          )}

          <div
            className={`menu-item ${activeMenu === "Request" ? "active" : ""}`}
            onClick={() => handleMenuClick("Request")}
          >
            <FaPaperPlane className="menu-icon" />
            <span>Feature Requests</span>
          </div>
        </div>

        <div className="sidebar-footer">
          <div className="menu-item upgrade-item" onClick={() => navigate('/report')}>
            <FaFileInvoiceDollar className="menu-icon" />
            <span>File a Report</span>
          </div>
          <div className="menu-item upgrade-item" onClick={() => handleMenuClick("Downloads")}>
            <FaDownload className="menu-icon" color={'green'} />
            <span>Download Pipsqueak Scanner</span>
          </div>
          {!isPro && (
            <div className="menu-item upgrade-item" onClick={handleUpgradeClick}>
              <FaCrown className="menu-icon" color="gold" />
              <span>Upgrade to Pro</span>
            </div>
          )}

          <div
            className="menu-item logout-item"
            onClick={handleLogout}
          >
            <FaSignOutAlt className="menu-icon" />
            <span>Logout</span>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <main style={{ maxHeight: 'calc(100vh - 70px)' }} className={`dashboard-content ${sidebarOpen ? 'sidebar-open' : 'sidebar-closed'}`}>
        <div className="content-container">
          {error && (
            <Alert variant="danger" dismissible onClose={() => setError(null)} className="error-alert">
              {error}
            </Alert>
          )}
          <div className="content-wrapper">
            {showUpdateBanner && (
              <div className="update-banner-premium">
                <div className="update-banner-content">
                  <div className="update-banner-icon-container">
                    <FaCloudUploadAlt />
                  </div>
                  <div className="update-banner-text">
                    <h5 className="update-banner-title">
                      New Update Available!
                      <span className="update-banner-badge">v{latestVersion}</span>
                    </h5>
                    <p className="update-banner-description">
                      A new version of the Pipsqueak Network Scanner application is available. Please update to the latest version to access the newest features, security updates, and performance improvements.
                    </p>
                  </div>
                </div>
                <div className="update-banner-actions">
                  <Button
                    onClick={() => handleMenuClick("Downloads")}
                    className="update-banner-btn"
                  >
                    Update Now
                  </Button>
                  <button
                    onClick={handleDismissUpdateBanner}
                    className="update-banner-close"
                    title="Dismiss notification"
                    aria-label="Dismiss notification"
                  >
                    <FaTimes size={16} />
                  </button>
                </div>
              </div>
            )}
            {renderContent()}
          </div>
        </div>
      </main>

      {/* Mobile Sidebar Overlay */}
      {isMobile && sidebarOpen && (
        <div className="sidebar-overlay" onClick={toggleSidebar}></div>
      )}

      {/* Logout Confirmation Modal */}
      <Modal
        show={showLogoutModal}
        onHide={() => setShowLogoutModal(false)}
        centered
        dialogClassName="w-auto"  // shrink to fit content
        backdropClassName={isDarkMode ? "bg-dark bg-opacity-75" : "bg-dark bg-opacity-50"}
      >
        <Modal.Header
          closeButton
          className={isDarkMode ? "bg-dark text-light" : "bg-light text-dark"}
        >
          <Modal.Title>Confirm Logout</Modal.Title>
        </Modal.Header>

        <Modal.Body className={isDarkMode ? "bg-dark text-light" : "bg-light text-dark"}>
          Are you sure you want to logout from your account?
        </Modal.Body>

        <Modal.Footer className={isDarkMode ? "bg-dark text-light" : "bg-light text-dark"}>
          <Button
            variant={isDarkMode ? "outline-light" : "secondary"}
            onClick={() => setShowLogoutModal(false)}
          >
            Cancel
          </Button>
          <Button variant="danger" onClick={confirmLogout}>
            Logout
          </Button>
        </Modal.Footer>
      </Modal>


      <Modal
        show={showLogoUploadModal}
        onHide={() => setShowLogoUploadModal(false)}
        centered
        dialogClassName="w-auto" // modal width adjusts to content
        backdropClassName={isDarkMode ? "bg-dark bg-opacity-75" : "bg-dark bg-opacity-50"}
      >
        <Modal.Header
          closeButton
          className={isDarkMode ? "bg-dark text-light" : "bg-light text-dark"}
        >
          <Modal.Title>Upload Tenant Logo</Modal.Title>
        </Modal.Header>

        <Modal.Body className={isDarkMode ? "bg-dark text-light" : "bg-light text-dark"}>
          {logoUploadError && (
            <Alert variant="danger" className="mb-3">
              {logoUploadError}
            </Alert>
          )}
          {logoUploadSuccess && (
            <Alert variant="success" className="mb-3">
              {logoUploadSuccess}
            </Alert>
          )}

          <Form.Group>
            <Form.Label>Select Logo Image</Form.Label>
            <Form.Control
              type="file"
              accept="image/*"
              onChange={handleFileSelect}
              className={isDarkMode ? "bg-dark text-light" : ""}
            />
            <Form.Text className={isDarkMode ? "text-light" : "text-muted"}>
              Maximum file size: 5MB. Supported formats: JPG, PNG, SVG, GIF.
            </Form.Text>
          </Form.Group>

          {selectedFile && (
            <div className="file-selection-container mt-3">
              <p>Selected file: {selectedFile.name}</p>
              <div className="file-actions">
                <Button
                  variant="outline-danger"
                  size="sm"
                  onClick={() => setSelectedFile(null)}
                >
                  <FaTimes className="me-1" />
                  Remove
                </Button>
                <Button
                  variant={isDarkMode ? "outline-light" : "primary"}
                  onClick={uploadLogo}
                  disabled={logoUploading}
                >
                  {logoUploading ? (
                    <>
                      <Spinner animation="border" size="sm" className="me-1" />
                      Uploading...
                    </>
                  ) : (
                    <>
                      <FaUpload className="me-1" />
                      Upload Logo
                    </>
                  )}
                </Button>
              </div>
            </div>
          )}
        </Modal.Body>

        <Modal.Footer className={isDarkMode ? "bg-dark text-light" : "bg-light text-dark"}>
          <Button
            variant={isDarkMode ? "outline-light" : "secondary"}
            onClick={() => setShowLogoUploadModal(false)}
          >
            Close
          </Button>
        </Modal.Footer>
      </Modal>


      {/* Upgrade to Pro Modal */}
      <Modal
        show={showUpgradeModal}
        onHide={() => setShowUpgradeModal(false)}
        centered
        backdrop="static"
        keyboard={false}
        size="lg"
        backdropClassName={isDarkMode ? "bg-dark bg-opacity-75" : "bg-dark bg-opacity-50"}
        dialogClassName={isDarkMode ? "upgrade-to-pro-modal dark-mode" : "upgrade-to-pro-modal"}
      >
        <Modal.Header
          closeButton
          className={isDarkMode ? "bg-dark text-light" : "bg-light text-dark"}
        >
          <Modal.Title>
            <FaCrown className="me-2 text-warning" />
            Upgrade to Pipsqueak Pro
          </Modal.Title>
        </Modal.Header>

        <Modal.Body className={isDarkMode ? "bg-dark text-light p-4" : "bg-light text-dark p-4"}>
          <div className="text-center mb-4">
            <h4 className="mb-2 fw-bold">Unlock Pipsqueak Pro</h4>
            <p className="text-muted mx-auto" style={{ maxWidth: '90%' }}>
              Experience the full power of Pipsqueak Pro and see how it can boost your productivity in 2026.
            </p>
          </div>

          <div className="border rounded p-0" style={{ borderColor: isDarkMode ? '#444' : '#dee2e6' }}>
            <div className="p-3">
              <div className="mb-3 p-3 rounded" style={{ backgroundColor: isDarkMode ? 'rgba(40,167,69,0.1)' : '#e8f5e9' }}>
                <h5 className="fw-bold mb-1" style={{ color: isDarkMode ? '#4ade80' : '#28a745', fontSize: '1rem' }}>Launch</h5>
                <div className="small">
                  <span className="text-muted">Jan 1 - Feb 28, 2026: </span>
                  <span className="fw-bold">Founders Pricing</span>
                </div>
                <div className="small">
                  2 Month only, get Pipsqueak Pro for <strong>$99/mo</strong> for the lifetime of your subscription.
                </div>
              </div>

              <div className="p-3 rounded" style={{ backgroundColor: isDarkMode ? 'rgba(255,193,7,0.1)' : '#fff8e1' }}>
                <h5 className="fw-bold mb-1" style={{ color: isDarkMode ? '#fbbf24' : '#ffc107', fontSize: '1rem' }}>Regular Pricing</h5>
                <div className="small">
                  Starting March 1, 2026: <strong>$250/mo</strong>
                </div>
              </div>
            </div>

            <div className="text-center p-2 border-top" style={{ backgroundColor: isDarkMode ? 'rgba(255,255,255,0.02)' : '#f8f9fa', borderColor: isDarkMode ? '#444' : '#dee2e6' }}>
              <p className="mb-0 small text-muted">Exclusive Pro Edition of Pipsqueak Network Scanner</p>
            </div>
          </div>
        </Modal.Body>


        <Modal.Footer className="border-0">
          <Button
            variant="secondary"
            onClick={() => setShowUpgradeModal(false)}
            style={{ backgroundColor: '#6c757d', border: 'none' }}
          >
            Maybe Later
          </Button>
          <Button
            variant="warning"
            onClick={handleUpgradeClick}
            className="d-flex align-items-center fw-bold"
            style={{ backgroundColor: '#ffc107', border: 'none', color: '#000' }}
          >
            <FaCrown className="me-2" />
            Upgrade to Pro
            <FaExternalLinkAlt className="ms-2" style={{ fontSize: '0.8rem' }} />
          </Button>
        </Modal.Footer>
      </Modal>


    </div>
  );
}

export default Dashboard;